import _ from 'lodash'
import React, { useEffect, useState } from 'react'
import { List, Map, fromJS } from 'immutable'
import { shallowEqual, useSelector } from 'react-redux'
import { STATIC_SF_FONT } from '../../../constants/FontFamily'
import { isSame } from '../../WidgetBuilder/UI/isSame'
import { Helmet } from 'react-helmet-async'
import { IS_SERVER } from '../../../constants/ssr'
import { createPortal } from 'react-dom'
import { useId } from 'react'

const SKIP_FONTS = [
  'Arial Black',
  'Arial Narrow',
  'Consolas',
  'Courier New',
  'Georgia',
  'Impact',
  'Tahoma',
  'Helvetica',
  'Times New Roman',
  'Verdana',
  'Salesforce Sans',
]

const injectScript = (async, defer, src, type, nomodule, isBody, fetchpriority) => {
  let script = document.querySelector(`script[src="${src}"]`)
  if (!script) {
    script = document.createElement('script')
    async && script.setAttribute('async', async)
    defer && script.setAttribute('defer', defer)
    type && script.setAttribute('type', type)
    fetchpriority && script.setAttribute('fetchpriority', fetchpriority)
    nomodule !== undefined && script.setAttribute('nomodule', nomodule)
    script.setAttribute('src', src)
    if (isBody) {
      document.body.appendChild(script)
    } else {
      document.head.appendChild(script)
    }
  }
}

export const Script = ({ async, defer, src, type, nomodule, isBody, fetchpriority }) => {
  useEffect(() => {
    injectScript(async, defer, src, type, nomodule, isBody, fetchpriority)
  }, [async, defer, src])

  return null
}

export const JSScript = ({ type, async, defer, html }) => {
  const id = useId()

  useEffect(() => {
    let script = document.getElementById(id)
    if (!script) {
      script = document.createElement('script')
      script.setAttribute('id', id)
      if (typeof document.body.prepend === 'function') {
        document.body.prepend(script)
      } else if (typeof document.body.insertBefore === 'function') {
        document.body.insertBefore(script, document.body.firstChild)
      } else {
        document.body.append(script)
      }
    }
    script.type = type
    script.defer = defer
    script.async = async
    script.innerHTML = html
  }, [type, async, defer, html])

  return null
}

const LottieAssetsLoader = () => {
  const isOptimizeLott = useSelector(
    (state) => state.getIn(['project', 'Settings', 'experiment', 'isOptimizeLott'], false),
    shallowEqual
  )
  const lottieElements = useSelector((state) => {
    let list = []
    const layoutMode = state.get('layoutMode', 'lg')

    const collectLottieFiles = (elem) => {
      if (elem.get('type') === 'Animation') {
        const options = elem.getIn(['props', layoutMode, 'options'], List())
        options.map((option) => {
          if (Map.isMap(option) && option.get('type') === 'lottie') {
            if (option.get('meta') && option.getIn(['meta', 'URL'])) {
              list.push({
                id: option.getIn(['meta', '_id']),
                src: option.getIn(['meta', 'URL']),
              })
            }
          }
        })
      }
    }

    state.getIn(['project', 'Settings', 'elements'], Map()).map(collectLottieFiles)
    if (state.get('page') && state.getIn(['pages', state.get('page')])) {
      state.getIn(['pages', state.get('page'), 'Props', 'elements'], Map()).map(collectLottieFiles)
    }

    return fromJS(_.uniqBy(list, (val) => val.id))
  }, shallowEqual)
  if (isOptimizeLott) {
    return lottieElements.map((entry) => (
      <Script key={entry.get('id')} type="text/javascript" src={entry.get('src')} fetchpriority="low" async isBody />
    ))
  } else {
    return lottieElements.map((entry) => (
      <Script key={entry.get('id')} type="text/javascript" defer src={entry.get('src')} />
    ))
  }
}

export function AsssetsManager({ onlyCSS = false, isPreview }) {
  const useCSS = useSelector((state) => state.getIn(['project', 'Settings', 'useCSS'], false))
  const useJS = useSelector((state) => state.getIn(['project', 'Settings', 'useJS'], false))
  const assets = useSelector((state) => state.getIn(['project', 'Settings', 'assets'], Map()), isSame)
  const [googleMapRendered, setGoogleMapRendered] = useState(false)

  const customFonts = useSelector(
    (state) =>
      state.getIn(['project', 'Settings', 'customFonts'], List()).filter((font) => font.get('fontSource') !== 'custom'),
    isSame
  )
  const customLoadFonts = useSelector(
    (state) =>
      state.getIn(['project', 'Settings', 'customFonts'], List()).filter((font) => font.get('fontSource') === 'custom'),
    isSame
  )

  const apiKeys = useSelector((state) => state.getIn(['project', 'Settings', 'ui', 'apiSettings'], Map()), isSame)

  const renderJSAsset = (asset, index) =>
    asset.get('embedType', '') != 'content' ? (
      <script
        ke={`js_${index}`}
        type="text/javascript"
        async={asset.get('async', false)}
        defer={asset.get('defer', false)}
        src={asset.get('src', '')}
      />
    ) : (
      <JSScript
        key={`js_${index}`}
        type="text/javascript"
        async={asset.get('async', false)}
        defer={asset.get('defer', false)}
        html={asset.get('content', '')}
      />
    )

  const renderStyle = (asset, index) =>
    asset.get('embedType', '') != 'content' ? (
      <link
        key={`styles_${index}`}
        type={asset.get('type', 'text/css')}
        media={asset.get('media', 'all')}
        rel={asset.get('rel', 'stylesheet')}
        {...(asset.get('crossorigin') ? { crossOrigin: asset.get('crossorigin', 'anonymous') } : {})}
        {...(asset.get('integrity', '') ? { integrity: asset.get('integrity', '') } : {})}
        href={asset.get('src', '')}
      />
    ) : (
      <style
        key={`styles_${index}`}
        // type={asset.get('type', 'text/css')}
        dangerouslySetInnerHTML={{ __html: asset.get('content', '') }}
      />
    )

  const renderCustomFonts = () => {
    let fonts = []
    customFonts.map(function (font) {
      if (font.get('value') !== 'SalesforceSans' && !SKIP_FONTS.includes(font.get('label'))) {
        fonts.push('family=' + String(font.get('label')).replace('Open Sans Hebrew', 'Open Sans').replace(/ /g, '+'))
      }
    })
    fonts = _.uniq(fonts)
    let customLoadFontTag = null
    if (customLoadFonts.size) {
      let html = customLoadFonts.map((font) => font.get('fontCSS', '')).toJS()
      customLoadFontTag = (
        <style
          key="custom-load-font"
          id="custom-load-font"
          type="text/css"
          dangerouslySetInnerHTML={{ __html: _.join(html, '\n') }}
        />
      )
    }

    return _.compact([
      fonts.length > 0 && (
        <link
          key="custom_fonts"
          rel="stylesheet"
          href={
            'https://fonts.googleapis.com/css2?' +
            fonts.join(':wght@100;200;300;400;700;900&') +
            ':wght@100;200;300;400;700;900&display=swap'
          }
          crossOrigin="anonymous"
        />
      ),
      customLoadFonts.size > 0 && customLoadFontTag,
    ])
  }

  const renderGoogleAPIKey = () => {
    if (!isPreview) {
      return ''
    }
    if (!googleMapRendered && !window.googleMapRendered) {
      var googleApiKey = '',
        googleRegion = ''
      if (apiKeys.get('isGoogleMap')) {
        if (apiKeys.get('googleMapRegion')) {
          googleRegion = `&region=${apiKeys.get('googleMapRegion')}`
        }
        googleApiKey = apiKeys.get('googleMapAPIKey')
        setGoogleMapRendered((googleMapRendered) => {
          if (!googleMapRendered) {
            const language = apiKeys.get('googleMapLanguage')
            let googleLanguage = ''
            if (language) googleLanguage = `&language=${apiKeys.get('googleMapLanguage')}`
            injectScript(
              undefined,
              true,
              `https://maps.googleapis.com/maps/api/js?key=${googleApiKey}${googleLanguage}&callback=googleMapInited${googleRegion}`
            )
            window.googleMapRendered = true
            return true
          }
          return googleMapRendered
        })
      }
    }

    return ''
  }

  const renderSalesforceSans = () => {
    let font = null

    if (customFonts.find((font) => font.get('value') === 'SalesforceSans')) {
      font = <style key="styles_SalesforceSans" type="text/css" dangerouslySetInnerHTML={{ __html: STATIC_SF_FONT }} />
    }

    return font
  }

  const js = assets.get('js', List())
  const styles = assets.get('styles', List())
  renderGoogleAPIKey()

  return (
    !IS_SERVER && (
      <>
        {createPortal(
          <>
            {useCSS && styles.map(renderStyle)}
            {renderCustomFonts()}
            {renderSalesforceSans()}
          </>,
          document.head,
          'assets'
        )}
        {useJS && !onlyCSS && createPortal(<>{js.map(renderJSAsset)}</>, document.body, 'assets')}
        <LottieAssetsLoader />
      </>
    )
  )
}
